import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api.js';
import TrendChart from '../components/TrendChart.jsx';
import CalendarView from '../components/CalendarView.jsx';
import { EVENTS, eventByKey } from '@shared/events.js';
import { formatTime, timeAgo } from '@shared/format.js';

function toInputDate(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function CoachStudent() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [event, setEvent] = useState('3x3');
  const [range, setRange] = useState('30d');
  const [trend, setTrend] = useState(null);
  const [calendar, setCalendar] = useState({ month: '', days: {} });
  const [sessions, setSessions] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentSession, setCommentSession] = useState(null); // null = 阶段点评
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [resetPw, setResetPw] = useState('');

  const now = new Date();
  const localMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const load = useCallback(async () => {
    const d = await api(`/coach/students/${id}`);
    setData(d);
    setExpiryDate(d.student?.expires_at ? toInputDate(d.student.expires_at) : '');
    setEvent((prev) => d.events.some((e) => e.event === prev) ? prev : d.student.main_event);
  }, [id]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      api(`/coach/students/${id}/trend?event=${event}&range=${range}`),
      api(`/coach/students/${id}/calendar?event=${event}&month=${localMonth}`),
      api(`/coach/students/${id}/sessions?event=${event}&limit=15`),
    ])
      .then(([t, c, s]) => {
        setTrend(t.trend);
        setCalendar({ month: c.month, days: c.days });
        setSessions(s.sessions);
      })
      .catch(() => {});
  }, [id, event, range]);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(''), 2500);
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setBusy(true);
    try {
      await api(`/coach/students/${id}/comments`, {
        method: 'POST',
        body: { content: commentText, session_id: commentSession },
      });
      setCommentText('');
      setCommentSession(null);
      load();
      flash('点评已发送，学生可在首页看到');
    } catch (err) {
      flash(err.message, false);
    } finally {
      setBusy(false);
    }
  };

  const deleteComment = async (cid) => {
    try {
      await api(`/coach/comments/${cid}`, { method: 'DELETE' });
      load();
    } catch {
      /* ignore */
    }
  };

  const setAccountDisabled = async (disabled) => {
    try {
      await api(`/coach/students/${id}/status`, { method: 'PATCH', body: { disabled } });
      await load();
      flash(disabled ? '该学生账号已停用，无法再登录' : '该学生账号已恢复');
    } catch (e) {
      flash(e.message, false);
    }
  };

  const saveExpiry = async () => {
    let value = null;
    if (expiryDate) {
      const [y, m, d] = expiryDate.split('-').map(Number);
      value = new Date(y, m - 1, d, 23, 59, 59).getTime();
    }
    try {
      await api(`/coach/students/${id}/expiry`, { method: 'PATCH', body: { expires_at: value } });
      await load();
      flash(value ? '到期时间已保存，到期后学生将无法登录' : '已设为长期有效');
    } catch (e) {
      flash(e.message, false);
    }
  };

  const clearExpiry = async () => {
    try {
      await api(`/coach/students/${id}/expiry`, { method: 'PATCH', body: { expires_at: null } });
      await load();
      flash('已设为长期有效');
    } catch (e) {
      flash(e.message, false);
    }
  };

  const resetPassword = async () => {
    const v = resetPw.trim();
    if (v.length < 6) {
      flash('临时密码至少 6 位', false);
      return;
    }
    setBusy(true);
    try {
      await api(`/coach/students/${id}/password`, { method: 'PATCH', body: { new_password: v } });
      setResetPw('');
      flash('密码已重置，请把新密码告诉学生');
    } catch (err) {
      flash(err.message, false);
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <div className="text-center text-sm text-slate-500 py-16">加载中…</div>;
  }

  const s = data.student;
  const ov = data.overview;
  const ev = eventByKey(event);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/coach" className="text-sm text-slate-500 hover:text-accent">‹ 返回列表</Link>
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent to-violet flex items-center justify-center text-ink-950 font-bold">
          {(s.nickname || '?').slice(0, 1)}
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-100">{s.nickname}</h1>
          <p className="text-xs text-slate-500">
            {s.email} · 开始学习 {s.started_at || '未设置'} · 注册于 {new Date(s.created_at).toLocaleDateString('zh-CN')}
          </p>
        </div>
        <span className={`ml-auto text-xs px-3 py-1 rounded-full ${(ov?.today_solves ?? 0) > 0 ? 'bg-good/15 text-good' : 'bg-warn/15 text-warn'}`}>
          {(ov?.today_solves ?? 0) > 0 ? '今日已打卡' : '今日未打卡'}
        </span>
      </div>

      {msg && (
        <div className={`text-sm rounded-xl px-4 py-2.5 ${msg.ok ? 'bg-good/10 text-good border border-good/20' : 'bg-danger/10 text-danger border border-danger/20'}`}>
          {msg.text}
        </div>
      )}

      {/* 项目切换 + 统计 */}
      <div className="flex flex-wrap gap-1.5">
        {EVENTS.map((e) => {
          const count = data.events.find((x) => x.event === e.key)?.total_solves || 0;
          return (
            <button
              key={e.key}
              onClick={() => setEvent(e.key)}
              className={`tab ${event === e.key ? 'tab-active' : 'tab-idle'}`}
            >
              {e.name}
              <span className="ml-1 text-[10px] opacity-70">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <Stat label="总成绩" value={ov?.total_solves ?? 0} suffix="把" />
        <Stat label="单次 PB" value={ov?.pb_single != null ? formatTime(ov.pb_single) : '—'} accent />
        <Stat label="PB Ao5" value={ov?.pb_ao5 != null ? formatTime(ov.pb_ao5) : '—'} />
        <Stat label="PB Ao12" value={ov?.pb_ao12 != null ? formatTime(ov.pb_ao12) : '—'} />
        <Stat label="当前 Ao5" value={ov?.current_ao5 != null ? formatTime(ov.current_ao5) : '—'} />
        <Stat label="连续打卡" value={ov?.streak ?? 0} suffix="天" />
      </div>

      {/* 账号管理 */}
      <div className="card p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-semibold text-slate-100">账号管理</h2>
            <p className="text-xs mt-0.5">
              {s.disabled ? (
                <span className="text-danger">账号已停用，该学生无法登录</span>
              ) : s.expires_at && Date.now() > s.expires_at ? (
                <span className="text-warn">课程已到期，学生登录会被拦截</span>
              ) : s.expires_at ? (
                <span className="text-good">有效期至 {new Date(s.expires_at).toLocaleDateString('zh-CN')}</span>
              ) : (
                <span className="text-good">长期有效</span>
              )}
            </p>
          </div>
          {s.disabled ? (
            <button className="btn-primary" onClick={() => setAccountDisabled(false)}>启用账号</button>
          ) : (
            <button className="btn-danger" onClick={() => setAccountDisabled(true)}>停用账号</button>
          )}
        </div>
        <div className="mt-4 flex items-end gap-3 flex-wrap">
          <div>
            <label className="label">课程到期日期（留空保存 = 长期有效）</label>
            <input type="date" className="input !w-56 !py-1.5" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
          </div>
          <button className="btn-primary !py-2 !text-xs" onClick={saveExpiry}>保存到期</button>
          <button className="btn-ghost !py-2 !text-xs text-slate-400" onClick={clearExpiry}>设为长期</button>
        </div>
        <div className="mt-4 flex items-end gap-3 flex-wrap border-t border-slate-700/50 pt-4">
          <div>
            <label className="label">重置密码（学生忘记密码时设一个临时密码）</label>
            <input
              type="text"
              className="input !w-56 !py-1.5"
              placeholder="输入至少 6 位的新密码"
              value={resetPw}
              onChange={(e) => setResetPw(e.target.value)}
            />
          </div>
          <button className="btn-ghost !py-2 !text-xs text-accent" onClick={resetPassword} disabled={busy}>
            重置密码
          </button>
        </div>
      </div>

      <div className="card p-5">
        <h2 className="font-semibold text-slate-100 mb-4">{ev.name} 成长曲线</h2>
        <TrendChart trend={trend} range={range} onRangeChange={setRange} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="card p-5">
          <h2 className="font-semibold text-slate-100 mb-4">打卡日历</h2>
          <CalendarView days={calendar.days} month={calendar.month} />
        </div>

        {/* 留言与点评 */}
        <div className="card p-5">
          <h2 className="font-semibold text-slate-100 mb-4">留言 / 点评</h2>
          <form onSubmit={addComment} className="space-y-3 mb-5">
            <textarea
              className="input !py-2.5 min-h-[90px] resize-y"
              placeholder={commentSession ? '针对这次打卡的点评…' : '阶段点评：鼓励、指导、目标…'}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
            />
            {commentSession && (
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>将点评到 #{commentSession} 次打卡</span>
                <button type="button" onClick={() => setCommentSession(null)} className="text-danger hover:underline">
                  改为阶段点评
                </button>
              </div>
            )}
            <button className="btn-primary" disabled={busy || !commentText.trim()}>
              {busy ? '发送中…' : '发送点评'}
            </button>
          </form>

          <div className="space-y-3 max-h-80 overflow-y-auto">
            {data.comments.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">还没有留言</p>
            ) : (
              data.comments.map((c) => (
                <div key={c.id} className="bg-slate-700/25 rounded-xl p-3.5 text-sm">
                  <p className="text-slate-200 leading-relaxed">{c.content}</p>
                  <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
                    <span>
                      {c.coach_name} · {timeAgo(c.created_at)}
                      {c.session_id && <span className="ml-2 text-accent/80">打卡 #{c.session_id}</span>}
                    </span>
                    <button onClick={() => deleteComment(c.id)} className="text-slate-600 hover:text-danger">删除</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* 打卡明细（可逐次点评） */}
      <div className="card p-5">
        <h2 className="font-semibold text-slate-100 mb-4">最近打卡明细</h2>
        {sessions.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center">该项目暂无打卡</p>
        ) : (
          <div className="space-y-4">
            {sessions.map((sess) => (
              <div key={sess.id} className="border border-slate-700/50 rounded-xl p-4">
                <div className="flex items-center flex-wrap gap-3 mb-3">
                  <span className="text-sm text-slate-300 font-medium">
                    #{sess.id} · {new Date(sess.created_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}
                  </span>
                  <span className="text-xs text-slate-500">{sess.count} 把</span>
                  <span className="text-xs font-mono text-slate-400 ml-auto">
                    平均 {sess.avg != null ? formatTime(sess.avg) : '—'} · 最佳 {sess.best != null ? formatTime(sess.best) : '—'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {sess.solves.map((sl, i) => (
                    <span
                      key={sl.id}
                      className={`px-2 py-1 rounded-md text-xs font-mono ${
                        sl.penalty === 'dnf' ? 'bg-danger/15 text-danger'
                        : sl.penalty === '+2' ? 'bg-warn/15 text-warn'
                        : 'bg-slate-700/40 text-slate-200'
                      }`}
                      title={sl.scramble}
                    >
                      {i + 1}: {sl.penalty === 'dnf' ? 'DNF' : formatTime(sl.time_ms + (sl.penalty === '+2' ? 2000 : 0))}
                    </span>
                  ))}
                </div>
                {sess.comments.map((c) => (
                  <div key={c.id} className="text-xs bg-accent/10 text-accent rounded-lg px-3 py-2 mb-2">
                    <span className="font-medium text-accent">留言：</span>{c.content}
                    <span className="text-slate-500 ml-2">{timeAgo(c.created_at)}</span>
                  </div>
                ))}
                <button
                  onClick={() => setCommentSession(sess.id)}
                  className="text-xs text-slate-400 hover:text-accent"
                >
                  点评这次打卡
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, suffix = '', accent = false }) {
  return (
    <div className="card px-4 py-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-lg font-semibold ${accent ? 'text-accent' : 'text-slate-100'}`}>
        {value}{suffix}
      </div>
    </div>
  );
}
